import { useCallback, useEffect, useRef,useState } from 'react';
import type { InvoiceResponse, SendPaymentRequestRequest } from '@store/generated/payments';

import {
  useConfirmPatientPaymentMethodMutation,
  useCreateStripeConnectMutation,
  useCreateStripeDashboardLinkMutation,
  useDeletePatientPaymentMethodMutation,
  useGetPatientInvoicesQuery,
  useGetPatientPaymentMethodsQuery,
  useGetStripeConnectStatusQuery,
  useSendPaymentRequestMutation,
  useSetDefaultPatientPaymentMethodMutation,
  useSetupPatientPaymentMethodMutation,
} from './api';

/**
 * Hook for managing Stripe Connect integration
 * Provides access to Stripe Connect status and onboarding
 */
export function useStripeConnect() {
   
  const { data: status, isLoading, error, refetch } = useGetStripeConnectStatusQuery();
  const [createStripeConnect, { isLoading: isCreating }] = useCreateStripeConnectMutation();

  const startOnboarding = useCallback(
    async (customReturnUrl?: string) => {
      try {
        // Use provided returnUrl or fallback to current URL
        const returnUrl = customReturnUrl || window.location.href;

        const result = await createStripeConnect({
          createStripeConnectRequest: {
            return_url: returnUrl,
            refresh_url: returnUrl,
          },
        }).unwrap();
        // The API returns an onboarding URL that the user should be redirected to
        return result;
      } catch (error) {
        console.error('Failed to start Stripe Connect onboarding:', error);
        throw error;
      }
    },
    [createStripeConnect],
  );

  return {
    status,
    isLoading,
    error,
    isCreating,
    startOnboarding,
    refetchStatus: refetch,
    // Computed properties
    hasStripeAccount: status?.has_account ?? false,
    isStripeEnabled: status?.charges_enabled && status.payouts_enabled,
    stripeAccountId: status?.stripe_account_id ?? null,
  };
}

/**
 * Hook for accessing Stripe Dashboard
 * Generates a link to the Stripe Express Dashboard
 */
export function useStripeDashboard() {
  const [createDashboardLink, { isLoading }] = useCreateStripeDashboardLinkMutation();

  const openDashboard = useCallback(async () => {
    try {
      const result = await createDashboardLink({
        createDashboardLinkRequest: {},
      }).unwrap();

      // Open the dashboard in a new tab
      window.open(result.dashboard_url, '_blank');
      return result;
    } catch (error) {
      console.error('Failed to create Stripe dashboard link:', error);
      throw error;
    }
  }, [createDashboardLink]);

  return {
    openDashboard,
    isLoading,
  };
}

/**
 * Hook for sending payment requests to patients
 * Allows providers to request payments via SMS
 */
export function useSendPaymentRequest() {
  const [sendPaymentRequest, result] = useSendPaymentRequestMutation();

  const mutate = useCallback(
    (
      request: SendPaymentRequestRequest,
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      const promise = sendPaymentRequest({
        sendPaymentRequestRequest: request,
      });

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [sendPaymentRequest],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook for managing patient payment methods
 * Provides access to payment methods list and mutation actions
 *
 * Only fetches data when Stripe Connect is enabled for the provider
 */
export function usePatientPaymentMethods(patientId: string) {
  const { isStripeEnabled, isLoading: isStripeLoading } = useStripeConnect();

  // Only fetch payment methods if Stripe is enabled
  const {
    data: paymentMethodsData,
    isLoading: isLoadingMethods,
    error: methodsError,
    refetch: refetchMethods,
  } = useGetPatientPaymentMethodsQuery(
    { patientId },
    { skip: !isStripeEnabled || !patientId },
  );

  // Mutations
  const [setupPaymentMethod, { isLoading: isSettingUp }] = useSetupPatientPaymentMethodMutation();
  const [confirmPaymentMethod, { isLoading: isConfirming }] = useConfirmPatientPaymentMethodMutation();
  const [deletePaymentMethod, { isLoading: isDeleting }] = useDeletePatientPaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] =
    useSetDefaultPatientPaymentMethodMutation();

  // Setup a new payment method - returns client_secret for Stripe Elements
  const setupNewPaymentMethod = useCallback(async () => {
    if (!patientId) throw new Error('Patient ID is required');

    const result = await setupPaymentMethod({ patientId }).unwrap();
    return result;
  }, [patientId, setupPaymentMethod]);

  // Confirm payment method after Stripe SetupIntent succeeds
  const confirmNewPaymentMethod = useCallback(async () => {
    if (!patientId) throw new Error('Patient ID is required');

    await confirmPaymentMethod({ patientId }).unwrap();
  }, [patientId, confirmPaymentMethod]);

  // Delete a payment method
  const deleteMethod = useCallback(
    async (paymentMethodId: string) => {
      if (!patientId) throw new Error('Patient ID is required');

      await deletePaymentMethod({ patientId, paymentMethodId }).unwrap();
    },
    [patientId, deletePaymentMethod],
  );

  // Set a payment method as default
  const setDefault = useCallback(
    async (paymentMethodId: string) => {
      if (!patientId) throw new Error('Patient ID is required');

      await setDefaultPaymentMethod({ patientId, paymentMethodId }).unwrap();
    },
    [patientId, setDefaultPaymentMethod],
  );

  return {
    // Data
    paymentMethods: paymentMethodsData?.payment_methods ?? [],
    totalMethods: paymentMethodsData?.total ?? 0,

    // Stripe Connect status
    isStripeEnabled,

    // Loading states
    isLoading: isStripeLoading || isLoadingMethods,
    isSettingUp,
    isConfirming,
    isDeleting,
    isSettingDefault,
    isMutating: isSettingUp || isConfirming || isDeleting || isSettingDefault,

    // Error
    error: methodsError,

    // Actions
    setupNewPaymentMethod,
    confirmNewPaymentMethod,
    deleteMethod,
    setDefault,
    refetch: refetchMethods,
  };
}

const DEFAULT_INVOICES_LIMIT = 5;

export type InvoiceStatusFilter = 'all' | 'paid' | 'open';

/**
 * Hook for managing patient invoices
 * Provides access to patient invoices with offset-based pagination
 *
 * Only fetches data when Stripe Connect is enabled for the provider
 */
export function usePatientInvoices(
  patientId: string,
  options?: { skip?: boolean; status?: InvoiceStatusFilter },
) {
  const { isStripeEnabled, isLoading: isStripeLoading } = useStripeConnect();

  // Track all loaded invoices and offset-based pagination
  const [allInvoices, setAllInvoices] = useState<InvoiceResponse[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Convert filter to API param (null means no filter)
  const statusParam = options?.status && options.status !== 'all' ? options.status : null;

  // Track which query's data we've already processed to avoid duplicates
  const processedQueryRef = useRef<string | undefined>(undefined);
  const getQueryKey = (o: number, s: string | null) => `${o}:${s ?? 'all'}`;

  // Determine if we should skip the query
  const shouldSkip = options?.skip || !isStripeEnabled || !patientId;

  // Query for initial data or next page
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    isFetching,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useGetPatientInvoicesQuery(
    {
      patientId,
      limit: DEFAULT_INVOICES_LIMIT,
      offset,
      status: statusParam,
    },
    { skip: shouldSkip },
  );

  // Reset when patientId or status filter changes
  useEffect(() => {
    setAllInvoices([]);
    setOffset(0);
    setHasMore(false);
    processedQueryRef.current = undefined;
  }, [patientId, statusParam]);

  // Update local state when data arrives
  useEffect(() => {
    const newInvoices = invoicesData?.invoices;
    if (!newInvoices) return;

    // Skip if we're still fetching - data might be stale from cache
    if (isFetching) return;

    // Create a unique key for this query (offset + status)
    const queryKey = getQueryKey(offset, statusParam);

    // Only process if we haven't already processed this query's data
    if (processedQueryRef.current === queryKey) return;

    if (offset === 0) {
      // Initial load or refresh - replace all
      setAllInvoices(newInvoices);
    } else {
      // Load more - append new invoices
      setAllInvoices(prev => [...prev, ...newInvoices]);
    }

    // If we got fewer results than the limit, there are no more pages
    setHasMore(newInvoices.length >= DEFAULT_INVOICES_LIMIT);
    processedQueryRef.current = queryKey;
  }, [invoicesData, offset, statusParam, isFetching]);

  // Load more invoices
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setOffset(prev => prev + DEFAULT_INVOICES_LIMIT);
    }
  }, [hasMore, isFetching]);

  // Refresh the list (reset pagination)
  const refresh = useCallback(() => {
    setAllInvoices([]);
    setOffset(0);
    setHasMore(false);
    processedQueryRef.current = undefined;
    refetchInvoices();
  }, [refetchInvoices]);

  // Show loading when: initial load, or fetching with empty list (filter change)
  const isInitialOrFilterLoading = isLoadingInvoices || (isFetching && allInvoices.length === 0);

  return {
    // Data
    invoices: allInvoices,

    // Stripe Connect status
    isStripeEnabled,

    // Loading states
    isLoading: isStripeLoading || isInitialOrFilterLoading,
    isLoadingMore: isFetching && offset > 0 && allInvoices.length > 0,

    // Pagination
    hasMore,

    // Error
    error: invoicesError,

    // Actions
    loadMore,
    refetch: refresh,
  };
}
